import React, { useCallback, useMemo, useState } from "react";

import array from "@/utils/array";
import obj from "@/utils/obj";

//

export type TabRender<Props extends unknown[]> = (...props: Props) => React.ReactNode;

export interface Tab<Props extends unknown[]> {
  render: TabRender<Props>;
  title?: string;
}

export interface TabWithId<T extends Record<string, unknown[]>> extends Tab<T[keyof T]> {
  id: keyof T;
}

export interface HistoryTab<T extends Record<string, unknown[]>> {
  tab: TabWithId<T>;
  props: T[keyof T];
}

export type TabsDefinition<T extends Record<string, unknown[]>> = {
  [K in keyof T]: Tab<T[K]> | TabRender<T[K]>;
};

export type TabsType<T extends Record<string, unknown[]>> = {
  [K in keyof T]: Tab<T[K]> & { id: K };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyTabsDefinition = TabsDefinition<Record<string, any[]>>;

export type ExtractTabsType<T extends AnyTabsDefinition> = {
  [K in keyof T]: T[K] extends Tab<infer Props>
    ? Props
    : T[K] extends (...arg: infer Props) => unknown
      ? Props
      : never;
};

interface UseTabsProps<T extends Record<string, unknown[]>, K extends keyof T> {
  tabs: TabsDefinition<T>;
  defaultTab: K;
  defaultProps: T[K];
  tab?: keyof T;
  props?: T[keyof T];
  onChangeTab?: (tab: TabWithId<T>, prevTab: TabWithId<T>) => void;
}

export type SetTab<T extends Record<string, unknown[]>> = <K extends keyof T>(
  tabName: K,
  ...props: T[K]
) => void;

export interface UseTabsReturn<T extends Record<string, unknown[]> = Record<string, unknown[]>>
  extends Record<string, unknown> {
  setTab: SetTab<T>;
  tab: TabWithId<T>;
  tabProps: T[keyof T];
  tabs: TabsType<T>;
  getPrevTab: () => null | HistoryTab<T>;
  canBack: () => boolean;
  back: () => void;
}

//

export function useTabs<T extends Record<string, unknown[]>, K extends keyof T>({
  tabs: definitionTabs,
  defaultTab: defaultTabName,
  defaultProps,
  tab: _tabName,
  props: _props,
  onChangeTab,
}: UseTabsProps<T, K>): UseTabsReturn<T> {
  const tabs = useMemo<TabsType<T>>(() => definitionToTabs(definitionTabs), [definitionTabs]);

  const defaultTab = tabs[defaultTabName];

  const [stateTab, _setTab] = useState<TabWithId<T>>({ ...defaultTab, id: defaultTabName });
  const [stateTabProps, setTabProps] = useState<T[keyof T]>(defaultProps);

  const tab: TabWithId<T> = _tabName ? tabs[_tabName] : stateTab;
  const tabProps: T[keyof T] = _props ? _props : stateTabProps;

  const [history, setHistory] = useState<HistoryTab<T>[]>([{ tab, props: tabProps }]);

  const setTab = useCallback(
    <K extends keyof T>(tabName: K, props: T[K], isBackMethod: boolean) => {
      const currentTab = tab;

      const _tabName: keyof T = tabName;
      const selectedTab = tabs[_tabName];

      setHistory((history) => {
        if (isBackMethod) return array.removeByIndex(history, -1);
        const slicedHistory = history.length >= 10 ? array.removeByIndex(history, 0) : history;
        const historyItem: HistoryTab<T> = { tab: selectedTab, props };
        return [...slicedHistory, historyItem];
      });

      _setTab(selectedTab);
      setTabProps(props);

      if (onChangeTab) onChangeTab(selectedTab, currentTab);
    },
    [onChangeTab, tab, tabs],
  );

  const setTabPublic = useCallback(
    <K extends keyof T>(tabName: K, ...props: T[K]) => {
      setTab(tabName, props, false);
    },
    [setTab],
  );

  const canBack = useCallback(() => {
    return history.length > 1;
  }, [history.length]);

  const getPrevTab = useCallback(() => {
    if (!canBack()) return null;
    return history[history.length - 2];
  }, [canBack, history]);

  const back = useCallback(() => {
    const prevTab = getPrevTab();
    if (prevTab === null) return null;
    setTab(prevTab.tab.id, prevTab.props, true);
  }, [getPrevTab, setTab]);

  return useMemo<UseTabsReturn<T>>(
    () => ({ back, canBack, getPrevTab, setTab: setTabPublic, tab, tabProps, tabs }),
    [back, canBack, getPrevTab, setTabPublic, tab, tabProps, tabs],
  );
}

//

function definitionToTabs<T extends Record<string, unknown[]>>(
  definitionTabs: TabsDefinition<T>,
): TabsType<T> {
  const tabs: Partial<TabsType<T>> = {};

  obj.entries(definitionTabs).map(({ key, value }) => {
    const tab: Tab<T[keyof T]> = typeof value === "function" ? { render: value } : value;
    const tabWithId: TabWithId<T> = { ...tab, id: key };
    tabs[key] = tabWithId;
  });

  return tabs as TabsType<T>;
}
